/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Grid, Box, Container, useMediaQuery, useTheme, Typography, Skeleton } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { ProgressBar } from "@components/ProgressBar";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import { useLazyGetGuaranteeConfigQuery } from "@api/catalogs";
import { GuaranteeConfigStruture } from "@interfaces/catalogs";
import { GUARANTEE_TYPE } from "@helpers/constantes";
import FormGarantiaInmobiliaria from "@components/Garantia/FormGarantiaInmobiliaria";
import FormGarantiaVehicular from "@components/Garantia/FormGarantiaVehicular";
import useStatusConfig from "@hooks/useStatusConfig";
import * as Sentry from "@sentry/react";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";

const SCREEN_STATUS = "CSIGA";

const InformacionGarantia = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const [getLoanAplication] = useLazyGetLoanApplicationByIdQuery();

  const applicationData = useSelector((state: RootState) => state.register.application);

  const [getGuaranteeTypeConfig] = useLazyGetGuaranteeConfigQuery();

  const [guaranteeConfig, setGuaranteeConfig] = useState<GuaranteeConfigStruture | null>(null);
  const [guaranteeConfigList, setGuaranteeConfigList] = useState<GuaranteeConfigStruture[]>([]);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getGuaranteeTypeConfig({ tax_code: applicationData.type }).unwrap();
        const application = await getLoanAplication(applicationData.id).unwrap();
        const guaranteeConfig = response.find((item) => item.guarantee_type_id === application.guarantee_type_id);
        setGuaranteeConfig(guaranteeConfig || null);
        setGuaranteeConfigList(response);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "InformacionGarantia",
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSelectedGuaranteeType = (valor: string) => {
    const nuevoConfig = guaranteeConfigList.find((item) => item.guarantee_type_id === valor);

    if (nuevoConfig) {
      setGuaranteeConfig(nuevoConfig);
    }
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.informacion_garantia?.titulo || " Información de tu garantía"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            {isLoading ? (
              <Skeleton variant="text" width="100%" height={200} />
            ) : (
              <React.Fragment>
                {guaranteeConfig?.guarantee_type.code === GUARANTEE_TYPE.vehicular.code ? (
                  <FormGarantiaVehicular
                    nextStatus={nextStatus}
                    guarantee_type_id={guaranteeConfig?.guarantee_type_id}
                    guaranteeConfigList={guaranteeConfigList}
                    onSeleccion={handleSelectedGuaranteeType}
                  />
                ) : (
                  <FormGarantiaInmobiliaria
                    nextStatus={nextStatus}
                    guarantee_type_id={String(guaranteeConfig?.guarantee_type_id)}
                    guaranteeConfigList={guaranteeConfigList}
                    onSeleccion={handleSelectedGuaranteeType}
                  />
                )}
              </React.Fragment>
            )}
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionGarantia;
